import {
  addDays,
  differenceInCalendarDays,
  isAfter,
  isBefore,
  isSameDay,
  subDays,
  startOfWeek,
  endOfWeek,
  subWeeks,
  startOfMonth,
  endOfMonth,
  subMonths,
  format,
} from "date-fns";

export const getYesterday = () => {
  return {
    fromDate: subDays(new Date(), 1),
    toDate: subDays(new Date(), 1),
  };
};

export const getWeek = () => {
  return {
    fromDate: startOfWeek(new Date()),
    toDate: endOfWeek(new Date()),
  };
};

export const getLastweek = () => {
  return {
    fromDate: startOfWeek(subWeeks(new Date(), 1)),
    toDate: endOfWeek(subWeeks(new Date(), 1)),
  };
};

export const getMonth = () => {
  return {
    fromDate: startOfMonth(new Date()),
    toDate: endOfMonth(new Date()),
  };
};

export const getLastMonth = () => {
  return {
    fromDate: startOfMonth(subMonths(new Date(), 1)),
    toDate: endOfMonth(subMonths(new Date(), 1)),
  };
};

export const getTimeAgo = (targetDate: Date) => {
  const currentDate = new Date();
  const timeDifference = currentDate.getTime() - targetDate.getTime();
  const millisecondsPerDay = 24 * 60 * 60 * 1000;
  const millisecondsPerMonth = millisecondsPerDay * 30;
  const millisecondsPerYear = millisecondsPerDay * 365;

  if (timeDifference < millisecondsPerMonth) {
    return `${Math.floor(timeDifference / millisecondsPerDay)}일`;
  } else if (timeDifference < millisecondsPerYear) {
    const months = Math.floor(timeDifference / millisecondsPerMonth);
    const days = Math.floor(
      (timeDifference % millisecondsPerMonth) / millisecondsPerDay
    );
    return `${months}개월 ${days}일`;
  } else {
    const years = Math.floor(timeDifference / millisecondsPerYear);
    const remainingTime = timeDifference % millisecondsPerYear;
    const months = Math.floor(remainingTime / millisecondsPerMonth);
    const days = Math.floor(
      (remainingTime % millisecondsPerMonth) / millisecondsPerDay
    );
    return `${years}년 ${months}개월 ${days}일`;
  }
};

export const getDateByType = (
  type: "today" | "yesterday" | "week" | "lastWeek" | "month" | "lastMonth"
) => {
  switch (type) {
    case "today":
      return {
        fromDate: new Date(),
        toDate: new Date(),
      };
    case "yesterday":
      return getYesterday();
    case "week":
      return getWeek();
    case "lastWeek":
      return getLastweek();
    case "month":
      return getMonth();
    case "lastMonth":
      return getLastMonth();
  }
};

export function getDatesBetween(
  start: Date,
  end: Date,
  inclusive = false
): Date[] {
  let diff = differenceInCalendarDays(end, start);
  if (inclusive) diff++;

  return [...Array(diff).keys()].map((offset) => addDays(start, offset));
}

export function getDayName(d: Date) {
  const names = ["일", "월", "화", "수", "목", "금", "토"];
  const day = d.getDay();
  return names[day];
}

export function isSameOrAfterDay(date: Date, compare: Date) {
  return isSameDay(date, compare) || isAfter(date, compare);
}

export function isSameOrBeforeDay(date: Date, compare: Date) {
  return isSameDay(date, compare) || isBefore(date, compare);
}

export function isBetweenDay(date: Date, start: Date, end: Date) {
  return isSameOrAfterDay(date, start) && isSameOrBeforeDay(date, end);
}

export function getDayNameByIndex(i: number) {
  const names = ["일", "월", "화", "수", "목", "금", "토"];
  return names[i];
}

/**
 *
 * @param {Date} d
 */
export function formatTime(d: any) {
  return format(d, "MM.dd a hh:mm");
}

export function formatIsoTime(isoTime: string) {
  const totalMins = isoTimeToMinutes(isoTime);
  const hours = Math.floor(totalMins / 60);
  const minutes = totalMins % 60;

  if (hours >= 12) {
    return `오후 ${appendZero(hours === 12 ? 12 : hours - 12)}:${appendZero(
      minutes
    )}`;
  }

  return `오전 ${appendZero(hours)}:${appendZero(minutes)}`;
}

export function appendZero(n: number) {
  if (n < 10) {
    return `0${n}`;
  }

  return `${n}`;
}

export function isoTimeToMinutes(isoTime: string) {
  const [hour, minutes] = isoTime.split(":");

  return Number(hour) * 60 + Number(minutes);
}

export function timeDiff(isoTimeBase: string, isoTimeSubtract: string) {
  return isoTimeToMinutes(isoTimeBase) - isoTimeToMinutes(isoTimeSubtract);
}

export function minutesToIsoTime(minutes: number) {
  return `${appendZero(Math.floor(minutes / 60))}:${appendZero(minutes % 60)}`;
}

// 백엔드 시간 스키마(00-23 시간)에 맞춰 "종료" 시간을 변환한다.
// 프론트 내부에서는 '24:00'으로 통일하고, API 송수신 경계에서만 '00:00'과 매핑.
// workEnd, lunchEnd 등
export const toViewEndTime = <T extends string | undefined | null>(
  time: T
): T => {
  return (time === "00:00" ? "24:00" : time) as T;
};
